import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useOrgTimezone, formatDateInTz, formatTimeInTz } from '../../utils/timezone';
import mfaService, {
  passkeyErrorMessage,
  passkeyUnavailableReason,
  type EnrolledMethods,
  type TrustedDevice,
  type TotpSetup,
} from '../../services/mfa';
import authService, { type PendingEmailChange } from '../../services/auth';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PageHeader from '../../components/PageHeader';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import LanguageCard from '../../components/settings/LanguageCard';

function MfaSettings() {
  const timezone = useOrgTimezone();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [methods, setMethods] = useState<EnrolledMethods | null>(null);
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // TOTP setup
  const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null);
  const [totpCode, setTotpCode] = useState('');

  // Email OTP enrol
  const [emailCodePending, setEmailCodePending] = useState(false);
  const [emailCode, setEmailCode] = useState('');

  // Passkey
  const [passkeyNickname, setPasskeyNickname] = useState('');

  // TOTP disable
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableInput, setShowDisableInput] = useState(false);

  // Email change
  const [pendingEmail, setPendingEmail] = useState<PendingEmailChange | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Account deletion
  const [showDeleteFlow, setShowDeleteFlow] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const [m, td] = await Promise.all([mfaService.getMethods(), mfaService.listTrustedDevices()]);
      setMethods(m);
      setTrustedDevices(td);
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Separate from refresh(): a failure here must not blank the whole page.
  useEffect(() => {
    authService.getPendingEmailChange().then(setPendingEmail).catch(() => {});
  }, []);

  const clearMessages = () => { setError(null); setInfo(null); };

  const passkeyUnavailable = passkeyUnavailableReason();
  // With a passkey or an authenticator app on, an emailed code is offered at
  // every sign-in regardless; the email switch only matters without them.
  const hasOtherMethod = !!methods && (methods.totp || methods.passkeys.length > 0);

  // ---------------- TOTP ----------------
  const startTotpSetup = async () => {
    clearMessages(); setBusy(true);
    try {
      const setup = await mfaService.setupTotp();
      setTotpSetup(setup);
    } catch (err: any) {
      setError(err?.message || 'TOTP setup failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async () => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.confirmTotp(totpCode);
      setInfo('Authenticator app enabled. Your other devices have been signed out.');
      setTotpSetup(null);
      setTotpCode('');
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Incorrect code');
    } finally {
      setBusy(false);
    }
  };

  const disableTotp = async () => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.disableTotp(disablePassword);
      setInfo('Authenticator app disabled.');
      setDisablePassword('');
      setShowDisableInput(false);
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Disable failed');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Email OTP ----------------
  const sendEmailOtpEnrol = async () => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.enableEmailOtp();
      setEmailCodePending(true);
      setInfo('Check your inbox for a 6-digit code.');
    } catch (err: any) {
      setError(err?.message || 'Failed to send code');
    } finally {
      setBusy(false);
    }
  };

  const confirmEmailOtp = async () => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.confirmEmailOtp(emailCode);
      setInfo('Email code verification enabled.');
      setEmailCodePending(false);
      setEmailCode('');
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Incorrect code');
    } finally {
      setBusy(false);
    }
  };

  const disableEmailOtp = async () => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.disableEmailOtp();
      setInfo('Email code verification disabled.');
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Disable failed');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Passkey ----------------
  const addPasskey = async () => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.registerPasskey(passkeyNickname || 'My device');
      setPasskeyNickname('');
      setInfo('Passkey added. Your other devices have been signed out, and each can sign back in with an emailed code.');
      await refresh();
    } catch (err) {
      setError(passkeyErrorMessage(err, 'Failed to add passkey'));
    } finally {
      setBusy(false);
    }
  };

  const removePasskey = async (id: number) => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.deletePasskey(id);
      setInfo('Passkey removed.');
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Failed to remove passkey');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Trusted devices ----------------
  const revokeDevice = async (id: number) => {
    clearMessages(); setBusy(true);
    try {
      await mfaService.revokeTrustedDevice(id);
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Email change ----------------
  const closeEmailForm = () => {
    setShowEmailForm(false);
    setNewEmail('');
    setEmailPassword('');
    setEmailError(null);
  };

  const requestEmailChange = async () => {
    setEmailError(null);
    setEmailBusy(true);
    try {
      const pending = await authService.requestEmailChange(newEmail.trim(), emailPassword);
      setPendingEmail(pending);
      closeEmailForm();
    } catch (err: any) {
      setEmailError(err?.message || 'Could not start the email change');
    } finally {
      setEmailBusy(false);
    }
  };

  const cancelEmailChange = async () => {
    setEmailBusy(true);
    try {
      await authService.cancelEmailChange();
      setPendingEmail(null);
    } catch (err: any) {
      setEmailError(err?.message || 'Could not cancel');
    } finally {
      setEmailBusy(false);
    }
  };

  // ---------------- Account deletion ----------------
  const handleDeleteAccount = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      await authService.deleteAccount(deletePassword);
      // Account is gone — clear the local session and return to login.
      await logout();
      navigate('/login', { replace: true });
    } catch (err: any) {
      setDeleteError(err?.message || 'Account deletion failed');
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50">
      <PageHeader
        title="Account & Security"
        subtitle="Manage your profile and two-factor authentication settings"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto py-6 px-4 space-y-6">
      {loading ? (
        <div className="p-8">Loading…</div>
      ) : (
      <>

      {/* -------- Profile -------- */}
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 divide-y divide-neutral-100">
          <div className="flex items-center justify-between py-3">
            <span className="text-sm font-medium text-neutral-600">Username</span>
            <span className="text-sm text-neutral-900">{user?.username}</span>
          </div>
          <div className="py-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-neutral-600 flex-shrink-0">Email</span>
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm text-neutral-900 break-all text-right">{user?.email}</span>
                {!showEmailForm && (
                  <Button variant="outline" size="sm" onClick={() => { setShowEmailForm(true); setEmailError(null); }}>
                    Change
                  </Button>
                )}
              </div>
            </div>

            {pendingEmail && !showEmailForm && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-2">
                <p>
                  Waiting for you to confirm <strong className="break-all">{pendingEmail.new_email}</strong>.
                  Open the link we sent there before {formatTimeInTz(pendingEmail.expires_at, timezone)}.
                  Until then you keep signing in with your current address.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setNewEmail(pendingEmail.new_email); setShowEmailForm(true); }}>
                    Send again
                  </Button>
                  <Button variant="outline" size="sm" onClick={cancelEmailChange} isLoading={emailBusy}>
                    Cancel change
                  </Button>
                </div>
              </div>
            )}

            {showEmailForm && (
              <div className="space-y-3">
                <p className="text-sm text-neutral-600">
                  We'll email a link to the new address. Your email only changes once you open it.
                </p>
                <Input
                  type="email"
                  label="New email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  autoComplete="email"
                />
                <Input
                  type="password"
                  label="Current password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  autoComplete="current-password"
                />
                {emailError && (
                  <div className="rounded-lg bg-error-50 border border-error-200 p-3 text-sm text-error-800">{emailError}</div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={requestEmailChange} isLoading={emailBusy} disabled={!newEmail.trim() || !emailPassword}>
                    Send confirmation link
                  </Button>
                  <Button variant="outline" onClick={closeEmailForm} disabled={emailBusy}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between py-3">
            <span className="text-sm font-medium text-neutral-600">Organisation</span>
            <span className="text-sm text-neutral-900">{user?.organisation_name || '—'}</span>
          </div>
          <div className="flex items-center justify-between py-3">
            <span className="text-sm font-medium text-neutral-600">User ID</span>
            <span className="font-mono text-xs bg-neutral-100 px-2 py-1 rounded text-neutral-700">{user?.id}</span>
          </div>
        </CardContent>
      </Card>

      <LanguageCard />

      {error && (
        <div className="rounded-lg bg-error-50 border border-error-200 p-3 text-sm text-error-800">{error}</div>
      )}
      {info && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">{info}</div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-neutral-800">Two-factor authentication</h2>
        <p className="text-sm text-neutral-500 mt-0.5">Add an extra layer of security. You can enrol multiple methods.</p>
      </div>

      {/* -------- Authenticator app -------- */}
      <Card>
        <CardHeader>
          <CardTitle>Authenticator app (TOTP)</CardTitle>
          <CardDescription>Google Authenticator, Microsoft Authenticator, 1Password, etc.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {methods?.totp ? (
            <>
              <p className="text-sm text-green-700 font-medium">✓ Enabled</p>
              {!showDisableInput ? (
                <Button variant="outline" onClick={() => setShowDisableInput(true)}>Disable</Button>
              ) : (
                <div className="space-y-2">
                  <Input
                    type="password"
                    label="Confirm password to disable"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button variant="danger" onClick={disableTotp} isLoading={busy} disabled={!disablePassword}>
                      Disable
                    </Button>
                    <Button variant="outline" onClick={() => { setShowDisableInput(false); setDisablePassword(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : totpSetup ? (
            <div className="space-y-4">
              <p className="text-sm text-neutral-700">
                Scan this QR code with your authenticator app, then enter the 6-digit code it generates.
              </p>
              <img src={totpSetup.qrDataUrl} alt="TOTP QR" className="mx-auto w-48 h-48" />
              <details className="text-sm text-neutral-600">
                <summary className="cursor-pointer">Can't scan? Enter manually</summary>
                <code className="block mt-2 p-2 bg-neutral-100 rounded text-xs break-all">{totpSetup.manualEntrySecret}</code>
              </details>
              <Input
                label="6-digit code"
                inputMode="numeric"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              />
              <div className="flex gap-2">
                <Button onClick={confirmTotp} isLoading={busy} disabled={totpCode.length !== 6}>Confirm</Button>
                <Button variant="outline" onClick={() => { setTotpSetup(null); setTotpCode(''); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button onClick={startTotpSetup} isLoading={busy}>Enable authenticator app</Button>
          )}
        </CardContent>
      </Card>

      {/* -------- Passkeys -------- */}
      <Card>
        <CardHeader>
          <CardTitle>Passkeys</CardTitle>
          <CardDescription>
            Sign in with your device's fingerprint, face, PIN or a hardware security key.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {methods && methods.passkeys.length > 0 && (
            <ul className="space-y-2">
              {methods.passkeys.map((pk) => (
                <li key={pk.id} className="flex items-center justify-between p-2 rounded border border-neutral-200">
                  <div>
                    <div className="text-sm font-medium text-neutral-900">{pk.nickname || 'Unnamed device'}</div>
                    <div className="text-xs text-neutral-500">
                      Added {formatDateInTz(pk.created_at, timezone)}
                      {pk.last_used_at && ` · last used ${formatDateInTz(pk.last_used_at, timezone)}`}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => removePasskey(pk.id)}>Remove</Button>
                </li>
              ))}
            </ul>
          )}

          <p className="text-sm text-neutral-600">
            A passkey works only where it is stored. One saved in a password manager works on every
            device that manager is on; one saved to this device alone works only here. Anywhere else,
            sign in with an emailed code and add a passkey there.
          </p>

          {passkeyUnavailable ? (
            <p className="text-sm text-neutral-600">{passkeyUnavailable}</p>
          ) : (
            <div className="space-y-2">
              <Input
                label="Nickname (e.g. Work MacBook)"
                value={passkeyNickname}
                onChange={(e) => setPasskeyNickname(e.target.value)}
                placeholder="My device"
              />
              <Button onClick={addPasskey} isLoading={busy}>Add passkey</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* -------- Email OTP -------- */}
      <Card>
        <CardHeader>
          <CardTitle>Email codes</CardTitle>
          <CardDescription>
            A one-time code sent to your email address.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-neutral-600">
            {hasOtherMethod
              ? 'Always offered when you sign in, so a device without your passkey or authenticator app can still get in. Nothing to switch on.'
              : 'Switch this on to be asked for an emailed code each time you sign in.'}
          </p>
          {methods?.email ? (
            <>
              <p className="text-sm text-green-700 font-medium">
                {hasOtherMethod ? '✓ Also kept on if you remove your other methods' : '✓ Enabled'}
              </p>
              <Button variant="outline" onClick={disableEmailOtp} isLoading={busy}>Disable</Button>
            </>
          ) : emailCodePending ? (
            <div className="space-y-2">
              <Input
                label="6-digit code"
                inputMode="numeric"
                maxLength={6}
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
              />
              <div className="flex gap-2">
                <Button onClick={confirmEmailOtp} isLoading={busy} disabled={emailCode.length !== 6}>Confirm</Button>
                <Button variant="outline" onClick={() => { setEmailCodePending(false); setEmailCode(''); }}>Cancel</Button>
              </div>
            </div>
          ) : hasOtherMethod ? null : (
            <Button onClick={sendEmailOtpEnrol} isLoading={busy}>Enable email codes</Button>
          )}
        </CardContent>
      </Card>

      {/* -------- Trusted devices -------- */}
      <Card>
        <CardHeader>
          <CardTitle>Trusted devices</CardTitle>
          <CardDescription>
            Devices where you've chosen "Trust this device for 30 days". They skip the 2FA step on login.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {trustedDevices.length === 0 ? (
            <p className="text-sm text-neutral-500">No trusted devices.</p>
          ) : (
            <ul className="space-y-2">
              {trustedDevices.map((d) => (
                <li key={d.id} className="flex items-center justify-between p-2 rounded border border-neutral-200">
                  <div>
                    <div className="text-sm font-medium text-neutral-900 truncate max-w-xs">{d.device_label || 'Unknown device'}</div>
                    <div className="text-xs text-neutral-500">
                      {d.ip_address || 'unknown IP'} · expires {formatDateInTz(d.expires_at, timezone)}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => revokeDevice(d.id)}>Revoke</Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* -------- Danger zone: account deletion -------- */}
      <div className="pt-4">
        <h2 className="text-lg font-semibold text-error-700">Delete account</h2>
        <p className="text-sm text-neutral-500 mt-0.5">
          Permanently delete your account and remove your personal information.
        </p>
      </div>

      <Card className="border-error-200">
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-start gap-3 rounded-lg bg-error-50 border border-error-200 p-3">
            <AlertTriangle className="h-5 w-5 text-error-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-error-800 space-y-1">
              <p className="font-medium">This cannot be undone.</p>
              <p className="text-error-700">
                Your name, email and other personal details will be permanently removed and you
                will be signed out and unable to log in again. Where we are legally or
                contractually required to (for example payroll/timesheet records and agreements
                you have entered into), the underlying records are retained in de-identified form.
              </p>
            </div>
          </div>

          {!showDeleteFlow ? (
            <Button
              variant="outline"
              className="text-error-600 border-error-200 hover:bg-error-50"
              onClick={() => {
                setShowDeleteFlow(true);
                setDeleteError(null);
                setDeletePassword('');
              }}
            >
              Delete my account
            </Button>
          ) : (
            <div className="space-y-3">
              <Input
                type="password"
                label="Enter your password to confirm"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                autoComplete="current-password"
              />
              {deleteError && (
                <div className="rounded-lg bg-error-50 border border-error-200 p-3 text-sm text-error-800">
                  {deleteError}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  onClick={handleDeleteAccount}
                  isLoading={deleting}
                  disabled={!deletePassword || deleting}
                >
                  Permanently delete my account
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteFlow(false);
                    setDeletePassword('');
                    setDeleteError(null);
                  }}
                  disabled={deleting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}
      </div>
    </div>
  );
}

export default MfaSettings;
